import { datejs } from '@block/utils';
import { CertificateType } from '../../types';
import { CertificateData } from './types';

// 证书标签映射
export const CERTIFICATE_LABELS: Record<CertificateType, string> = {
  [CertificateType.OnlineVerification]: '教育部学籍在线验证报告',
  [CertificateType.StudyProof]: '在读证明',
  [CertificateType.Diploma]: '毕业证书',
  [CertificateType.Professional]: '职业资格证书',
  [CertificateType.Degree]: '学位证书',
  [CertificateType.ForeignAuth]: '留服认证书',
};

// 证书类型到原因类型的映射
export const REASON_TYPE_MAP = {
  [CertificateType.Diploma]: 1, // 毕业证书
  [CertificateType.Degree]: 2, // 学位证书
  [CertificateType.ForeignAuth]: 3, // 留服认证书
  [CertificateType.OnlineVerification]: 4, // 学籍在线验证报告
};

// 原因类型到证书类型的映射
export const REASON_TYPE_REVERSE_MAP = {
  1: CertificateType.Diploma, // 毕业证书
  2: CertificateType.Degree, // 学位证书
  3: CertificateType.ForeignAuth, // 留服认证书
  4: CertificateType.OnlineVerification, // 学籍在线验证报告
};

// 需要显示证书编码的证书类型
export const CERTIFICATE_TYPES_WITH_CODE = [
  CertificateType.Diploma, // 毕业证书
  CertificateType.Degree, // 学位证书
];

// 需要OCR识别的证书类型
export const OCR_CERTIFICATE_TYPES = [
  CertificateType.Diploma, // 毕业证书
  CertificateType.Degree, // 学位证书
];

// 原因索引枚举
export const REASON_INDEX = {
  // 毕业证书相关
  DIPLOMA_LOSS: 2, // 毕业证书丢失

  // 学位证书相关
  DEGREE_LOSS: 5, // 学位证书丢失

  // 留服认证相关
  FOREIGN_AUTH_PENDING: 8, // 留服认证申请中

  // 学籍在线验证报告相关
  STUDY_PROOF: 9, // 在读证明
  STUDENT_STATUS_SCREENSHOT: 10, // 学籍截图
};

// 需要上传文件的原因索引列表
export const NEED_UPLOAD_FILE_REASON_INDEXES = [
  REASON_INDEX.DIPLOMA_LOSS, // 毕业证书丢失
  REASON_INDEX.DEGREE_LOSS, // 学位证书丢失
  REASON_INDEX.FOREIGN_AUTH_PENDING, // 留服认证申请中
  REASON_INDEX.STUDY_PROOF, // 在读证明
  REASON_INDEX.STUDENT_STATUS_SCREENSHOT, // 学籍截图
];

// 学历等级枚举
export const EDUCATION_LEVEL = {
  HIGH_SCHOOL: '64', // 高中（中专）及以下
  COLLEGE: '67', // 大学专科
  BACHELOR: '68', // 大学本科
  MASTER: '69', // 硕士研究生
  DOCTOR: '70', // 博士研究生
};

// 学历等级映射
export const EDUCATION_LEVEL_MAP = {
  [EDUCATION_LEVEL.HIGH_SCHOOL]: '高中（中专）及以下',
  [EDUCATION_LEVEL.COLLEGE]: '大学专科',
  [EDUCATION_LEVEL.BACHELOR]: '大学本科',
  [EDUCATION_LEVEL.MASTER]: '硕士研究生',
  [EDUCATION_LEVEL.DOCTOR]: '博士研究生',
};

// 原因标签映射
export const REASON_LABEL_MAP = {
  [CertificateType.Diploma]: {
    [REASON_INDEX.DIPLOMA_LOSS]: {
      // 毕业证书丢失 - 高中/中专及以下或毕业时间早于2001年
      special: (data: any) =>
        data.diplomaDictIndex === EDUCATION_LEVEL.HIGH_SCHOOL ||
        (data.diplomaDictIndex !== EDUCATION_LEVEL.HIGH_SCHOOL && data.endDate < datejs('2001-1-1').valueOf()),
      specialLabel: '学校出具的已盖章的毕业证明',
      defaultLabel: '《教育部学历证书电子注册备案表》',
    },
  },
  [CertificateType.Degree]: {
    [REASON_INDEX.DEGREE_LOSS]: '《中国高等教育学位在线认证报告》', // 学位证书丢失
  },
  [CertificateType.ForeignAuth]: {
    [REASON_INDEX.FOREIGN_AUTH_PENDING]: '《教育部留学服务中心学历学位认证书》申请截图', // 留服认证申请中
  },
  [CertificateType.OnlineVerification]: {
    [REASON_INDEX.STUDY_PROOF]: '学校开具的《在读证明》', // 在读证明
    [REASON_INDEX.STUDENT_STATUS_SCREENSHOT]: '学校官网的学籍截图', // 学籍截图
  },
};

// 重置数据模板
export const getResetData = (codeItemKey?: string, attachmentFileType?: number): CertificateData => ({
  unableUpload: false,
  [codeItemKey]: '',
  reason: {
    reasonIndex: undefined,
    reasonType: undefined,
    reasonName: '',
    url: '',
    tipsName: '',
    urlType: undefined,
  },
  reasonType: undefined,
  unableUploadReason: undefined,
  fileId: undefined,
  fileName: undefined,
  fileUrl: undefined,
  attachmentFileType,
});

// 获取证书标签函数
export const getReasonLabel = (certificateType: CertificateType, reasonIndex: number, data: any): string => {
  // 获取当前证书类型的原因映射
  const certReasonMap = REASON_LABEL_MAP[certificateType];
  if (!certReasonMap) return '上传附件';

  // 获取特定原因的标签
  const reasonLabel = certReasonMap[reasonIndex];
  if (!reasonLabel) return '上传附件';

  // 如果是对象，说明有特殊逻辑
  if (typeof reasonLabel === 'object' && reasonLabel.special) {
    return reasonLabel.special(data) ? reasonLabel.specialLabel : reasonLabel.defaultLabel;
  }

  // 如果是字符串，直接返回
  return reasonLabel;
};

/**
 * 特殊情况的文件类型
 * 1、在勾选了无法上传的时候会置为-999
 * 2、在原因选择了清空的时候，会置为-999
 *  */
export const SPECIAL_ATTACHMENT_FILE_TYPE = -999;
